(() => {
  'use strict';

  if (window.SoulChemistAudioPreview) return;

  const TRIGGER_SELECTOR = '[data-sc-audio-trigger]';
  const PRODUCT_BOOTSTRAP_SELECTOR = '[data-sc-product-audio-bootstrap]';
  const PRODUCT_TRACK_SELECTOR = '[data-sc-product-audio-track]';

  const PLAYER_ID = 'ScAudioPreviewPlayer';

  const PROGRESS_MAX = 1000;
  const VOLUME_STORAGE_KEY = 'scAudioPreviewVolume';


  /* =========================================================
     HELPERS
  ========================================================= */

  const clean = (value) => {
    return (value || '').trim();
  };


  const formatTime = (seconds) => {
    if (
      !Number.isFinite(seconds) ||
      seconds < 0
    ) {
      return '0:00';
    }

    const total = Math.floor(seconds);
    const minutes = Math.floor(total / 60);
    const remainder = String(total % 60).padStart(2, '0');

    return `${minutes}:${remainder}`;
  };


  const readStoredVolume = () => {
    try {
      const stored = Number(
        window.localStorage.getItem(VOLUME_STORAGE_KEY)
      );

      if (
        Number.isFinite(stored) &&
        stored >= 0 &&
        stored <= 1
      ) {
        return stored;
      }
    } catch (error) {
      // Ignore storage failures.
    }

    return 0.85;
  };


  const titleFromAudioUrl = (src) => {
    if (!src) {
      return 'Audio preview';
    }

    let filename = src;

    try {
      filename = new URL(
        src,
        window.location.origin
      ).pathname
        .split('/')
        .pop() || src;
    } catch (error) {
      filename = src
        .split('/')
        .pop()
        .split('?')[0];
    }


    try {
      filename = decodeURIComponent(
        filename
      );
    } catch (error) {
      // Keep original filename.
    }


    filename = filename.replace(
      /\.(wav|mp3|aif|aiff)$/i,
      ''
    );


    filename = filename.replace(
      /\.xpm_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
      ''
    );


    filename = filename.replace(
      /\.xpm$/i,
      ''
    );


    filename = filename.replace(
      /_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
      ''
    );


    filename = filename
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();


    return (
      filename ||
      'Audio preview'
    );
  };


  /* =========================================================
     STATE
  ========================================================= */

  const state = {
    audio: new Audio(),

    track: null,

    playlist: [],
    currentIndex: -1,
    playlistProductId: '',

    loading: false,
    switching: false,

    playReportPending: false,
    playSource: 'card',

    closeTimer: null,

    milestones: new Set(),

    syncQueued: false,

    previousVolume:
      readStoredVolume() || 0.85,

    productBootstrapped: false,

    productMode: false,

    autoplayAttempted: false
  };


  state.audio.preload = 'metadata';
  state.audio.volume = readStoredVolume();


  /* =========================================================
     PLAYER UI
  ========================================================= */

  const getPlayer = () => {
    return document.getElementById(
      PLAYER_ID
    );
  };


  const installListingPlayerTightFix = () => {
    if (
      document.getElementById(
        'ScAudioListingPlayerTightFix'
      ) ||
      document.body?.classList.contains(
        'template-product'
      )
    ) {
      return;
    }

    const style =
      document.createElement(
        'style'
      );

    style.id =
      'ScAudioListingPlayerTightFix';

    style.textContent = `
      @media screen and (min-width: 981px) {
        body:not(.template-product) #ScAudioPreviewPlayer.sc-audio-player {
          width: min(1180px, calc(100vw - 28px)) !important;
        }

        body:not(.template-product) #ScAudioPreviewPlayer .sc-audio-player__shell {
          padding-right: 4px !important;
        }

        body:not(.template-product) #ScAudioPreviewPlayer .sc-audio-player__close {
          margin-right: 0 !important;
        }
      }
    `;

    document.head.appendChild(
      style
    );
  };


  const getUI = () => {
    const player = getPlayer();

    if (!player) return null;

    return {
      player,

      dock: document.querySelector(
        '[data-sc-player-dock]'
      ),

      artworkWrap: player.querySelector(
        '[data-sc-player-artwork-wrap]'
      ),

      artworkLink: player.querySelector(
        '[data-sc-player-artwork-link]'
      ),

      image: player.querySelector(
        '[data-sc-player-image]'
      ),

      toggle: player.querySelector(
        '[data-sc-player-toggle]'
      ),

      kicker: player.querySelector(
        '[data-sc-player-kicker]'
      ),

      title: player.querySelector(
        '[data-sc-player-title]'
      ),

      titleLink: player.querySelector(
        '[data-sc-player-title-link]'
      ),

      subtitle: player.querySelector(
        '[data-sc-player-subtitle]'
      ),

      range: player.querySelector(
        '[data-sc-player-range]'
      ),

      current: player.querySelector(
        '[data-sc-player-current]'
      ),

      duration: player.querySelector(
        '[data-sc-player-duration]'
      ),

      volume: player.querySelector(
        '[data-sc-player-volume]'
      ),

      mute: player.querySelector(
        '[data-sc-player-mute]'
      ),

      previous: player.querySelector(
        '[data-sc-player-previous]'
      ),

      next: player.querySelector(
        '[data-sc-player-next]'
      ),

      productLink: player.querySelector(
        '[data-sc-player-product-link]'
      ),

      close: player.querySelector(
        '[data-sc-player-close]'
      ),

      status: player.querySelector(
        '[data-sc-player-status]'
      ),

      playlistToggle: player.querySelector(
        '[data-sc-player-playlist-toggle]'
      ),

      playlistCount: player.querySelector(
        '[data-sc-player-playlist-count]'
      ),

      playlistPanel: player.querySelector(
        '[data-sc-player-playlist]'
      ),

      playlistClose: player.querySelector(
        '[data-sc-player-playlist-close]'
      ),

      playlistTitle: player.querySelector(
        '[data-sc-player-playlist-title]'
      ),

      playlistTracks: player.querySelector(
        '[data-sc-player-playlist-tracks]'
      )
    };
  };


  /* =========================================================
     TRACK BUILDERS
  ========================================================= */

  const trackFromTrigger = (
    trigger
  ) => ({
    id: clean(
      trigger.dataset.scProductId
    ),

    handle: clean(
      trigger.dataset.scProductHandle
    ),

    src: clean(
      trigger.dataset.scAudioSrc
    ),

    audioTitle: clean(
      trigger.dataset.scAudioTitle
    ),

    productTitle: clean(
      trigger.dataset.scProductTitle
    ),

    productType: clean(
      trigger.dataset.scProductType
    ),

    productUrl: clean(
      trigger.dataset.scProductUrl
    ),

    image: clean(
      trigger.dataset.scImage
    )
  });


  const trackFromProductBootstrap = (
    bootstrap
  ) => {
    const src = clean(
      bootstrap.dataset.scAudioSrc
    );

    return {
      id: clean(
        bootstrap.dataset.scProductId
      ),

      handle: clean(
        bootstrap.dataset.scProductHandle
      ),

      src,

      audioTitle:
        titleFromAudioUrl(src),

      productTitle: clean(
        bootstrap.dataset.scProductTitle
      ),

      productType: clean(
        bootstrap.dataset.scProductType
      ),

      productUrl: clean(
        bootstrap.dataset.scProductUrl
      ),

      image: clean(
        bootstrap.dataset.scImage
      )
    };
  };


  const playlistFromBootstrap = (
    bootstrap
  ) => {
    const base =
      trackFromProductBootstrap(
        bootstrap
      );


    const nodes =
      Array.from(
        bootstrap.querySelectorAll(
          PRODUCT_TRACK_SELECTOR
        )
      );


    return nodes
      .map(
        (node, index) => {

          const src = clean(
            node.dataset.scTrackSrc
          );


          if (!src) {
            return null;
          }


          return {
            ...base,

            src,

            audioTitle:
              titleFromAudioUrl(
                src
              ),

            /*
              IMPORTANT:

              Playlist number is determined ONLY
              by its position in Audio previews.

              First file  = 01
              Second file = 02
              Third file  = 03
              etc.

              Filename numbers are ignored.
            */
            playlistIndex:
              index,

            trackNumber:
              index + 1
          };

        }
      )
      .filter(Boolean);
  };


  const sameTrack = (
    candidate
  ) => (
    state.track &&
    candidate &&
    state.track.src === candidate.src &&
    state.track.id === candidate.id
  );


  /* =========================================================
     ACCESSIBILITY + ANALYTICS
  ========================================================= */

  const announce = (
    message
  ) => {
    const ui = getUI();

    if (ui?.status) {
      ui.status.textContent =
        message;
    }
  };


  const analyticsPayload = (
    eventName,
    extra = {}
  ) => {

    const detail = {
      event:
        eventName,

      product_id:
        state.track?.id || '',

      product_handle:
        state.track?.handle || '',

      product_title:
        state.track?.productTitle || '',

      product_type:
        state.track?.productType || '',

      preview_title:
        state.track?.audioTitle || '',

      preview_url:
        state.track?.src || '',

      current_time:
        Number.isFinite(
          state.audio.currentTime
        )
          ? Math.round(
              state.audio.currentTime *
              10
            ) / 10
          : 0,

      duration:
        Number.isFinite(
          state.audio.duration
        )
          ? Math.round(
              state.audio.duration *
              10
            ) / 10
          : 0,

      volume:
        state.audio.muted
          ? 0
          : state.audio.volume,

      playlist_index:
        state.currentIndex,

      playlist_size:
        state.playlist.length,

      ...extra
    };


    document.dispatchEvent(
      new CustomEvent(
        'sc:audio-preview',
        {
          detail
        }
      )
    );


    if (
      Array.isArray(
        window.dataLayer
      )
    ) {
      window.dataLayer.push(
        detail
      );
    }
  };


  /* =========================================================
     TITLES
  ========================================================= */

  const getDisplayTitle = () => {
    if (!state.track) {
      return 'Audio preview';
    }

    return (
      state.track.audioTitle ||
      state.track.productTitle ||
      'Audio preview'
    );
  };


  const getSubtitleBase = () => {
    if (!state.track) {
      return 'Audio preview';
    }

    return (
      state.track.productTitle ||
      state.track.productType ||
      'Audio preview'
    );
  };


  const updateMetadataText = () => {
    const ui = getUI();

    if (
      !ui ||
      !state.track
    ) {
      return;
    }


    const duration =
      state.audio.duration;


    const subtitleParts = [
      getSubtitleBase()
    ];


    if (
      Number.isFinite(duration) &&
      duration > 0
    ) {
      subtitleParts.push(
        `${formatTime(duration)} preview`
      );
    }


    if (
      state.playlist.length > 1 &&
      state.currentIndex >= 0
    ) {
      subtitleParts.push(
        `${state.currentIndex + 1}/${state.playlist.length}`
      );
    }


    if (ui.kicker) {

      if (state.loading) {

        ui.kicker.textContent =
          'LOADING PREVIEW';

      } else if (
        state.audio.ended
      ) {

        ui.kicker.textContent =
          'PREVIEW COMPLETE';

      } else if (
        !state.audio.paused
      ) {

        ui.kicker.textContent =
          'NOW PLAYING';

      } else {

        ui.kicker.textContent =
          'AUDIO PREVIEW';

      }
    }


    if (ui.title) {
      ui.title.textContent =
        getDisplayTitle();
    }


    if (ui.subtitle) {
      ui.subtitle.textContent =
        subtitleParts.join(' · ');
    }
  };


  /* =========================================================
     CARD SYNC
  ========================================================= */

  const syncTriggerStates = () => {
    const triggers =
      document.querySelectorAll(
        TRIGGER_SELECTOR
      );


    const isPlaying =
      Boolean(
        state.track &&
        !state.audio.paused &&
        !state.audio.ended
      );


    const isLoading =
      Boolean(
        state.track &&
        state.loading
      );


    const isEnded =
      Boolean(
        state.track &&
        state.audio.ended
      );


    triggers.forEach(
      (trigger) => {

        const candidate =
          trackFromTrigger(
            trigger
          );


        const active =
          sameTrack(
            candidate
          );


        trigger.classList.toggle(
          'is-playing',
          active &&
          isPlaying
        );


        trigger.classList.toggle(
          'is-loading',
          active &&
          isLoading
        );


        trigger.setAttribute(
          'aria-pressed',
          String(
            active &&
            isPlaying
          )
        );


        let action =
          'Play';


        if (
          active &&
          isPlaying
        ) {
          action =
            'Pause';
        }


        if (
          active &&
          isEnded
        ) {
          action =
            'Replay';
        }


        trigger.setAttribute(
          'aria-label',
          `${action} preview: ${candidate.productTitle}`
        );

      }
    );
  };


  const queueTriggerSync = () => {
    if (state.syncQueued) {
      return;
    }


    state.syncQueued = true;


    requestAnimationFrame(
      () => {

        state.syncQueued =
          false;

        syncTriggerStates();

      }
    );
  };


  /* =========================================================
     VOLUME
  ========================================================= */

  const updateVolumeUI = () => {
    const ui = getUI();

    if (!ui) return;


    const percentage =
      Math.round(
        state.audio.volume *
        100
      );


    const muted =
      state.audio.muted ||
      percentage === 0;


    ui.player.classList.toggle(
      'is-muted',
      muted
    );


    if (ui.volume) {

      ui.volume.value =
        String(
          percentage
        );


      ui.volume.style.setProperty(
        '--sc-volume',
        `${percentage}%`
      );


      ui.volume.setAttribute(
        'aria-valuenow',
        String(
          percentage
        )
      );
    }


    if (ui.mute) {
      ui.mute.setAttribute(
        'aria-label',
        muted
          ? 'Restore preview volume'
          : 'Mute preview'
      );
    }
  };


  const setVolume = (
    percentage
  ) => {
    const safe =
      Math.min(
        100,
        Math.max(
          0,
          Number(percentage) ||
          0
        )
      );


    const volume =
      safe / 100;


    if (volume > 0) {
      state.previousVolume =
        volume;

      state.audio.muted =
        false;
    }


    state.audio.volume =
      volume;


    try {
      window.localStorage.setItem(
        VOLUME_STORAGE_KEY,
        String(volume)
      );
    } catch (error) {
      // Ignore storage failure.
    }


    updateVolumeUI();
  };


  const toggleMute = () => {
    const muted =
      state.audio.muted ||
      state.audio.volume === 0;


    if (muted) {

      state.audio.volume =
        state.previousVolume > 0
          ? state.previousVolume
          : 0.85;

      state.audio.muted =
        false;

    } else {

      state.previousVolume =
        state.audio.volume ||
        0.85;

      state.audio.muted =
        true;

    }


    updateVolumeUI();
  };


  /* =========================================================
     PLAYLIST CONTROLS
  ========================================================= */

  const updatePlaylistControls = () => {
    const ui = getUI();

    if (!ui) return;


    const hasPlaylist =
      state.playlist.length > 1;


    if (ui.playlistToggle) {
      ui.playlistToggle.hidden =
        !hasPlaylist;
    }


    if (ui.previous) {
      ui.previous.hidden =
        !hasPlaylist;

      ui.previous.disabled =
        !hasPlaylist ||
        state.currentIndex <= 0;
    }


    if (ui.next) {
      ui.next.hidden =
        !hasPlaylist;

      ui.next.disabled =
        !hasPlaylist ||
        state.currentIndex >=
          state.playlist.length - 1;
    }
  };


  /* =========================================================
     PLAYER STATE
  ========================================================= */

  const updatePlayerState = () => {
    const ui = getUI();

    if (!ui) return;


    const playing =
      Boolean(
        state.track &&
        !state.audio.paused &&
        !state.audio.ended
      );


    const ended =
      Boolean(
        state.track &&
        state.audio.ended
      );


    ui.player.classList.toggle(
      'is-playing',
      playing
    );


    ui.player.classList.toggle(
      'is-loading',
      Boolean(
        state.track &&
        state.loading
      )
    );


    ui.player.classList.toggle(
      'is-ended',
      ended
    );


    if (ui.toggle) {

      let label =
        'Play audio preview';


      if (state.loading) {

        label =
          'Loading audio preview';

      } else if (ended) {

        label =
          'Replay audio preview';

      } else if (playing) {

        label =
          'Pause audio preview';

      }


      ui.toggle.setAttribute(
        'aria-label',
        label
      );
    }


    updateMetadataText();
    updateVolumeUI();
    updatePlaylistActiveState();
    updatePlaylistControls();
    queueTriggerSync();
  };


  /* =========================================================
     PROGRESS
  ========================================================= */

  const getBufferedPercentage = () => {
    const duration =
      state.audio.duration;


    if (
      !Number.isFinite(duration) ||
      duration <= 0 ||
      state.audio.buffered.length === 0
    ) {
      return 0;
    }


    try {

      return Math.min(
        100,
        Math.max(
          0,
          (
            state.audio.buffered.end(
              state.audio.buffered.length -
              1
            ) /
            duration
          ) *
          100
        )
      );

    } catch (error) {

      return 0;

    }
  };


  const updateProgress = () => {
    const ui = getUI();

    if (!ui?.range) {
      return;
    }


    const duration =
      state.audio.duration;

    const current =
      state.audio.currentTime;


    const progress =
      Number.isFinite(duration) &&
      duration > 0 &&
      Number.isFinite(current)
        ? Math.min(
            PROGRESS_MAX,
            Math.max(
              0,
              (
                current /
                duration
              ) *
              PROGRESS_MAX
            )
          )
        : 0;


    const playedPercentage =
      (
        progress /
        PROGRESS_MAX
      ) *
      100;


    const bufferedPercentage =
      Math.max(
        playedPercentage,
        getBufferedPercentage()
      );


    ui.range.value =
      String(
        Math.round(
          progress
        )
      );


    ui.range.setAttribute(
      'aria-valuenow',
      String(
        Math.round(
          progress
        )
      )
    );


    ui.range.style.setProperty(
      '--sc-progress',
      `${playedPercentage}%`
    );


    ui.range.style.setProperty(
      '--sc-buffered',
      `${bufferedPercentage}%`
    );


    if (ui.current) {
      ui.current.textContent =
        formatTime(current);
    }


    if (ui.duration) {
      ui.duration.textContent =
        formatTime(duration);
    }


    updateMetadataText();
  };


  const seekFromRange = (
    range
  ) => {
    const duration =
      state.audio.duration;


    if (
      !Number.isFinite(duration) ||
      duration <= 0
    ) {
      return;
    }


    state.audio.currentTime =
      (
        Number(range.value) /
        PROGRESS_MAX
      ) *
      duration;


    updateProgress();
  };


  /* =========================================================
     PLAYER OPEN / CLOSE
  ========================================================= */

  const hideDock = () => {
    const ui = getUI();

    if (!ui?.dock) return;


    ui.dock.classList.remove(
      'is-visible'
    );


    ui.dock.setAttribute(
      'aria-hidden',
      'true'
    );


    window.setTimeout(
      () => {

        if (
          !ui.dock.classList.contains(
            'is-visible'
          )
        ) {
          ui.dock.hidden =
            true;
        }

      },
      220
    );
  };


  const showDock = () => {
    const ui = getUI();

    if (
      !ui?.dock ||
      !state.track
    ) {
      return;
    }


    ui.dock.hidden =
      false;


    ui.dock.setAttribute(
      'aria-hidden',
      'false'
    );


    requestAnimationFrame(
      () => {

        ui.dock.classList.add(
          'is-visible'
        );

      }
    );
  };


  const openPlayer = () => {
    const ui = getUI();

    if (!ui) return;


    window.clearTimeout(
      state.closeTimer
    );


    hideDock();


    ui.player.hidden =
      false;


    ui.player.setAttribute(
      'aria-hidden',
      'false'
    );


    requestAnimationFrame(
      () => {

        ui.player.classList.add(
          'is-open'
        );

      }
    );
  };


  const closePlaylist = () => {
    const ui = getUI();

    if (!ui) return;


    if (ui.playlistPanel) {
      ui.playlistPanel.hidden =
        true;
    }


    if (ui.playlistToggle) {
      ui.playlistToggle.setAttribute(
        'aria-expanded',
        'false'
      );
    }


    ui.player.classList.remove(
      'is-playlist-open'
    );
  };


  const openPlaylist = () => {
    const ui = getUI();

    if (
      !ui ||
      !ui.playlistPanel ||
      state.playlist.length < 2
    ) {
      return;
    }


    ui.playlistPanel.hidden =
      false;


    if (ui.playlistToggle) {
      ui.playlistToggle.setAttribute(
        'aria-expanded',
        'true'
      );
    }


    ui.player.classList.add(
      'is-playlist-open'
    );
  };


  const togglePlaylist = () => {
    const ui = getUI();

    if (
      !ui ||
      !ui.playlistPanel
    ) {
      return;
    }


    if (
      ui.playlistPanel.hidden
    ) {
      openPlaylist();
    } else {
      closePlaylist();
    }
  };


  const closePlayer = () => {
    const ui = getUI();

    if (!ui) return;


    closePlaylist();


    if (
      state.track &&
      !state.audio.paused
    ) {

      analyticsPayload(
        'audio_preview_pause',
        {
          source: 'close'
        }
      );


      state.audio.pause();
    }


    state.loading =
      false;

    state.playReportPending =
      false;


    updatePlayerState();
    updateProgress();


    ui.player.classList.remove(
      'is-open'
    );


    ui.player.setAttribute(
      'aria-hidden',
      'true'
    );


    state.closeTimer =
      window.setTimeout(
        () => {

          ui.player.hidden =
            true;

          showDock();

        },
        280
      );
  };


  /* =========================================================
     PRODUCT PAGE CTA
  ========================================================= */

  const getProductBootstrap = () => {
    return document.querySelector(
      PRODUCT_BOOTSTRAP_SELECTOR
    );
  };


  const setCtaText = (
    element,
    text
  ) => {
    if (!element) return;


    let label =
      element.querySelector(
        '[data-sc-player-cta-text]'
      );


    if (!label) {

      const textNode =
        Array.from(
          element.childNodes
        ).find(
          (node) =>
            node.nodeType ===
              Node.TEXT_NODE &&
            node.textContent.trim()
        );


      label =
        document.createElement(
          'span'
        );


      label.setAttribute(
        'data-sc-player-cta-text',
        ''
      );


      if (textNode) {

        element.replaceChild(
          label,
          textNode
        );

      } else {

        element.insertBefore(
          label,
          element.firstChild
        );

      }
    }


    label.textContent =
      text;
  };


  const configureProductCta = () => {
    if (!state.productMode) {
      return;
    }


    const ui = getUI();

    if (!ui?.productLink) {
      return;
    }


    ui.productLink.dataset.scProductCartCta =
      'true';


    ui.productLink.href =
      '#';


    ui.productLink.setAttribute(
      'role',
      'button'
    );


    ui.productLink.setAttribute(
      'aria-label',
      `Add ${state.track?.productTitle || 'product'} to cart`
    );


    setCtaText(
      ui.productLink,
      'ADD TO CART'
    );
  };


  const findNativeAddButton = () => {
    const productInfo =
      document.querySelector(
        'product-info[form]'
      );


    const formId =
      productInfo?.getAttribute(
        'form'
      ) || '';


    if (formId) {

      const form =
        document.getElementById(
          formId
        );


      const insideButton =
        form?.querySelector(
          [
            'button[type="submit"][name="add"]:not([disabled])',
            '.product-form__submit:not([disabled])'
          ].join(',')
        );


      if (insideButton) {
        return insideButton;
      }


      const externalButtons =
        Array.from(
          document.querySelectorAll(
            'button[type="submit"][name="add"][form]'
          )
        );


      const externalButton =
        externalButtons.find(
          (button) =>
            button.getAttribute(
              'form'
            ) === formId &&
            !button.disabled
        );


      if (externalButton) {
        return externalButton;
      }
    }


    return document.querySelector(
      [
        'form[is="product-form"] button[type="submit"][name="add"]:not([disabled])',
        '.product-form__submit:not([disabled])'
      ].join(',')
    );
  };


  const forceOpenNativeCartDrawer = () => {
    document.dispatchEvent(
      new CustomEvent(
        'cart:refresh',
        {
          bubbles: true,

          detail: {
            open: true
          }
        }
      )
    );
  };


  const addCurrentProductToCart = (
    cta
  ) => {
    if (
      !cta ||
      cta.getAttribute(
        'aria-busy'
      ) === 'true'
    ) {
      return;
    }


    const nativeButton =
      findNativeAddButton();


    if (!nativeButton) {

      setCtaText(
        cta,
        'UNAVAILABLE'
      );


      window.setTimeout(
        () => {
          setCtaText(
            cta,
            'ADD TO CART'
          );
        },
        1200
      );


      return;
    }


    cta.setAttribute(
      'aria-busy',
      'true'
    );


    setCtaText(
      cta,
      'ADDING…'
    );


    let settled =
      false;


    const cleanup = () => {
      document.removeEventListener(
        'ajaxProduct:added',
        handleAdded
      );

      document.removeEventListener(
        'ajaxProduct:error',
        handleError
      );
    };


    const restore = () => {
      cta.removeAttribute(
        'aria-busy'
      );

      setCtaText(
        cta,
        'ADD TO CART'
      );
    };


    const handleAdded = () => {
      if (settled) return;

      settled =
        true;

      cleanup();


      setCtaText(
        cta,
        'ADDED'
      );


      forceOpenNativeCartDrawer();


      window.setTimeout(
        restore,
        900
      );
    };


    const handleError = () => {
      if (settled) return;

      settled =
        true;

      cleanup();


      setCtaText(
        cta,
        'TRY AGAIN'
      );


      window.setTimeout(
        restore,
        1200
      );
    };


    document.addEventListener(
      'ajaxProduct:added',
      handleAdded
    );


    document.addEventListener(
      'ajaxProduct:error',
      handleError
    );


    nativeButton.click();


    window.setTimeout(
      () => {

        if (settled) {
          return;
        }


        settled =
          true;

        cleanup();

        restore();

      },
      5000
    );
  };


  /* =========================================================
     RENDER CURRENT TRACK
  ========================================================= */

  const renderTrack = () => {
    const ui = getUI();

    if (
      !ui ||
      !state.track
    ) {
      return;
    }


    const productUrl =
      state.track.productUrl ||
      '#';


    const productLabel =
      state.track.productTitle ||
      getDisplayTitle();


    [
      ui.titleLink,
      ui.artworkLink
    ].forEach(
      (link) => {

        if (!link) return;


        link.href =
          productUrl;


        link.setAttribute(
          'aria-label',
          `View product: ${productLabel}`
        );

      }
    );


    if (
      ui.productLink &&
      !state.productMode
    ) {

      ui.productLink.href =
        productUrl;


      ui.productLink.setAttribute(
        'aria-label',
        `View product: ${productLabel}`
      );
    }


    if (
      ui.image &&
      ui.artworkWrap
    ) {

      if (
        state.track.image
      ) {

        ui.image.src =
          state.track.image;


        ui.image.alt =
          `${productLabel} cover`;


        ui.artworkWrap.classList.remove(
          'is-empty'
        );

      } else {

        ui.image.removeAttribute(
          'src'
        );


        ui.image.alt =
          '';


        ui.artworkWrap.classList.add(
          'is-empty'
        );

      }
    }


    configureProductCta();

    updateMetadataText();
    updateProgress();
    updatePlaylistActiveState();
    updatePlaylistControls();
  };


  /* =========================================================
     PLAYLIST
  ========================================================= */

  const clearPlaylist = () => {
    const ui = getUI();


    state.playlist =
      [];

    state.currentIndex =
      -1;

    state.playlistProductId =
      '';


    if (ui?.playlistTracks) {
      ui.playlistTracks.innerHTML =
        '';
    }


    if (ui?.playlistToggle) {
      ui.playlistToggle.hidden =
        true;

      ui.playlistToggle.setAttribute(
        'aria-expanded',
        'false'
      );
    }


    if (ui?.previous) {
      ui.previous.hidden =
        true;
    }


    if (ui?.next) {
      ui.next.hidden =
        true;
    }


    if (ui?.playlistCount) {
      ui.playlistCount.textContent =
        '0';
    }


    if (ui?.playlistPanel) {
      ui.playlistPanel.hidden =
        true;
    }


    ui?.player?.classList.remove(
      'is-playlist-open'
    );
  };


  const updatePlaylistActiveState = () => {
    const ui = getUI();

    if (!ui?.playlistTracks) {
      return;
    }


    const buttons =
      ui.playlistTracks.querySelectorAll(
        '[data-sc-playlist-track]'
      );


    buttons.forEach(
      (button) => {

        const index =
          Number(
            button.dataset.scPlaylistIndex
          );


        const active =
          index ===
          state.currentIndex;


        button.classList.toggle(
          'is-active',
          active
        );


        button.setAttribute(
          'aria-current',
          active
            ? 'true'
            : 'false'
        );


        const indicator =
          button.querySelector(
            '[data-sc-playlist-state]'
          );


        if (indicator) {

          if (
            active &&
            !state.audio.paused &&
            !state.audio.ended
          ) {

            indicator.textContent =
              'Playing';

          } else if (active) {

            indicator.textContent =
              'Selected';

          } else {

            indicator.textContent =
              '';

          }
        }

      }
    );


    updatePlaylistControls();
  };


  const createPlaylistTrackButton = (
    track,
    index
  ) => {
    const button =
      document.createElement(
        'button'
      );


    button.type =
      'button';


    button.className =
      'sc-audio-player__playlist-track';


    button.dataset.scPlaylistTrack =
      '';


    button.dataset.scPlaylistIndex =
      String(index);


    button.setAttribute(
      'aria-label',
      `Play ${track.audioTitle}`
    );


    const number =
      document.createElement(
        'span'
      );


    number.className =
      'sc-audio-player__playlist-track-number';


    /*
      NUMBER IS ALWAYS BASED ON PLAYLIST ORDER.

      Audio previews order:
      position 1 = 01
      position 2 = 02
      position 3 = 03
      ...
    */
    number.textContent =
      String(
        index + 1
      ).padStart(
        2,
        '0'
      );


    const play =
      document.createElement(
        'span'
      );


    play.className =
      'sc-audio-player__playlist-track-play';


    play.setAttribute(
      'aria-hidden',
      'true'
    );


    play.innerHTML =
      '<svg viewBox="0 0 24 24" focusable="false"><path d="M8.35 5.18a1 1 0 0 1 1.53-.85l9.2 6.82a1.06 1.06 0 0 1 0 1.7l-9.2 6.82a1 1 0 0 1-1.53-.85V5.18Z"/></svg>';


    const copy =
      document.createElement(
        'span'
      );


    copy.className =
      'sc-audio-player__playlist-track-copy';


    const title =
      document.createElement(
        'strong'
      );


    title.textContent =
      track.audioTitle ||
      'Audio preview';


    const subtitle =
      document.createElement(
        'small'
      );


    subtitle.textContent =
      track.productTitle ||
      track.productType ||
      'Sample Packs by Soul Chemist';


    copy.appendChild(
      title
    );


    copy.appendChild(
      subtitle
    );


    const stateLabel =
      document.createElement(
        'span'
      );


    stateLabel.className =
      'sc-audio-player__playlist-track-state';


    stateLabel.dataset.scPlaylistState =
      '';


    button.appendChild(
      number
    );


    button.appendChild(
      play
    );


    button.appendChild(
      copy
    );


    button.appendChild(
      stateLabel
    );


    return button;
  };


  const renderPlaylist = () => {
    const ui = getUI();

    if (!ui) return;


    if (
      state.playlist.length < 2
    ) {

      updatePlaylistControls();

      return;
    }


    if (ui.playlistToggle) {
      ui.playlistToggle.hidden =
        false;
    }


    if (ui.playlistCount) {
      ui.playlistCount.textContent =
        String(
          state.playlist.length
        );
    }


    if (ui.playlistTitle) {
      ui.playlistTitle.textContent =
        state.playlist[0]
          ?.productTitle ||
        'Product previews';
    }


    if (ui.playlistTracks) {

      ui.playlistTracks.innerHTML =
        '';


      const fragment =
        document.createDocumentFragment();


      state.playlist.forEach(
        (track, index) => {

          fragment.appendChild(
            createPlaylistTrackButton(
              track,
              index
            )
          );

        }
      );


      ui.playlistTracks.appendChild(
        fragment
      );
    }


    updatePlaylistActiveState();
    updatePlaylistControls();
  };


  const setPlaylist = (
    playlist
  ) => {
    if (
      !Array.isArray(playlist) ||
      playlist.length === 0
    ) {

      clearPlaylist();

      return;
    }


    state.playlist =
      playlist;


    state.playlistProductId =
      playlist[0]?.id ||
      '';


    state.currentIndex =
      0;


    renderPlaylist();
  };


  const getPlaylistTrack = (
    index
  ) => {
    if (
      index < 0 ||
      index >=
        state.playlist.length
    ) {
      return null;
    }


    return (
      state.playlist[index] ||
      null
    );
  };


  const findPlaylistIndex = (
    track
  ) => {
    if (!track) {
      return -1;
    }


    return state.playlist.findIndex(
      (item) => (
        item.src ===
          track.src &&
        item.id ===
          track.id
      )
    );
  };


  /* =========================================================
     AUDIO LOADING
  ========================================================= */

  const handlePlaybackError = (
    error
  ) => {
    state.loading =
      false;

    state.playReportPending =
      false;


    updatePlayerState();


    announce(
      'The audio preview could not be played.'
    );


    const ui = getUI();


    if (ui?.kicker) {
      ui.kicker.textContent =
        'PREVIEW UNAVAILABLE';
    }


    analyticsPayload(
      'audio_preview_error',
      {
        message:
          error?.message ||
          'Playback error'
      }
    );


    console.warn(
      '[Soul Chemist Audio Preview]',
      error
    );
  };


  const requestPlay = (
    source = 'card',
    options = {}
  ) => {
    if (!state.track?.src) {
      return;
    }


    if (state.audio.ended) {
      state.audio.currentTime =
        0;

      state.milestones.clear();
    }


    state.loading =
      true;

    state.playReportPending =
      true;

    state.playSource =
      source;


    updatePlayerState();


    const result =
      state.audio.play();


    if (
      result &&
      typeof result.catch ===
        'function'
    ) {

      result.catch(
        (error) => {

          if (
            options.autoplayAttempt ===
              true &&
            (
              error?.name ===
                'NotAllowedError' ||
              error?.name ===
                'AbortError'
            )
          ) {

            state.loading =
              false;

            state.playReportPending =
              false;


            updatePlayerState();


            announce(
              `Preview ready: ${state.track.productTitle}`
            );


            return;
          }


          handlePlaybackError(
            error
          );
        }
      );
    }
  };


  const pauseFromUser = (
    source = 'card'
  ) => {
    if (
      !state.track ||
      state.audio.paused
    ) {
      return;
    }


    analyticsPayload(
      'audio_preview_pause',
      {
        source
      }
    );


    state.audio.pause();
  };


  const loadTrackSource = (
    nextTrack
  ) => {
    state.switching =
      true;


    state.audio.pause();


    state.switching =
      false;


    state.track =
      nextTrack;


    state.loading =
      false;

    state.playReportPending =
      false;

    state.milestones.clear();


    state.audio.src =
      nextTrack.src;


    state.audio.currentTime =
      0;


    state.audio.load();


    const playlistIndex =
      findPlaylistIndex(
        nextTrack
      );


    if (
      playlistIndex >= 0
    ) {
      state.currentIndex =
        playlistIndex;
    }


    renderTrack();
    updatePlayerState();
  };


  const loadAndPlay = (
    nextTrack,
    source = 'card'
  ) => {
    if (!nextTrack?.src) {
      return;
    }


    loadTrackSource(
      nextTrack
    );


    openPlayer();


    requestPlay(
      source
    );
  };


  const loadWithoutPlaying = (
    nextTrack
  ) => {
    if (!nextTrack?.src) {
      return;
    }


    loadTrackSource(
      nextTrack
    );


    openPlayer();

    updateProgress();


    announce(
      `Preview ready: ${state.track.productTitle}`
    );
  };


  const toggleTrack = (
    trigger
  ) => {
    const candidate =
      trackFromTrigger(
        trigger
      );


    if (!candidate.src) {
      return;
    }


    if (
      state.playlistProductId &&
      state.playlistProductId !==
        candidate.id
    ) {

      state.productMode =
        false;

      clearPlaylist();

    }


    if (
      !sameTrack(candidate)
    ) {

      loadAndPlay(
        candidate,
        'card'
      );

      return;
    }


    openPlayer();


    if (
      state.audio.paused ||
      state.audio.ended
    ) {

      requestPlay(
        'card'
      );

    } else {

      pauseFromUser(
        'card'
      );

    }
  };


  /* =========================================================
     NEXT / PREVIOUS
  ========================================================= */

  const playPlaylistIndex = (
    index,
    source = 'playlist'
  ) => {
    const track =
      getPlaylistTrack(
        index
      );


    if (!track) {
      return false;
    }


    state.currentIndex =
      index;


    loadAndPlay(
      track,
      source
    );


    return true;
  };


  const playNext = (
    autoplay = true
  ) => {
    if (
      state.playlist.length < 2 ||
      state.currentIndex < 0
    ) {
      return false;
    }


    const nextIndex =
      state.currentIndex +
      1;


    if (
      nextIndex >=
      state.playlist.length
    ) {
      return false;
    }


    const nextTrack =
      getPlaylistTrack(
        nextIndex
      );


    if (!nextTrack) {
      return false;
    }


    state.currentIndex =
      nextIndex;


    if (autoplay) {

      loadAndPlay(
        nextTrack,
        'playlist-next'
      );

    } else {

      loadWithoutPlaying(
        nextTrack
      );

    }


    return true;
  };


  const playPrevious = (
    autoplay = true
  ) => {
    if (
      state.playlist.length < 2 ||
      state.currentIndex < 0
    ) {
      return false;
    }


    if (
      state.audio.currentTime >
      3
    ) {

      state.audio.currentTime =
        0;


      updateProgress();


      if (
        autoplay &&
        state.audio.paused
      ) {
        requestPlay(
          'playlist-previous'
        );
      }


      return true;
    }


    const previousIndex =
      state.currentIndex -
      1;


    if (
      previousIndex < 0
    ) {
      return false;
    }


    const previousTrack =
      getPlaylistTrack(
        previousIndex
      );


    if (!previousTrack) {
      return false;
    }


    state.currentIndex =
      previousIndex;


    if (autoplay) {

      loadAndPlay(
        previousTrack,
        'playlist-previous'
      );

    } else {

      loadWithoutPlaying(
        previousTrack
      );

    }


    return true;
  };


  /* =========================================================
     PLAYBACK MILESTONES
  ========================================================= */

  const reportMilestones = () => {
    if (!state.track) return;


    const duration =
      state.audio.duration;

    const current =
      state.audio.currentTime;


    if (
      !Number.isFinite(duration) ||
      duration <= 0 ||
      !Number.isFinite(current)
    ) {
      return;
    }


    const ratio =
      current /
      duration;


    [25, 50, 75].forEach(
      (milestone) => {

        if (
          ratio >=
            milestone / 100 &&
          !state.milestones.has(
            milestone
          )
        ) {

          state.milestones.add(
            milestone
          );


          analyticsPayload(
            `audio_preview_${milestone}`,
            {
              milestone
            }
          );

        }

      }
    );
  };


  /* =========================================================
     PRODUCT BOOTSTRAP
  ========================================================= */

  const bootstrapProductPlayer = () => {
    if (
      state.productBootstrapped
    ) {
      return;
    }


    const bootstrap =
      getProductBootstrap();


    if (!bootstrap) {
      return;
    }


    state.productMode =
      true;


    const playlist =
      playlistFromBootstrap(
        bootstrap
      );


    if (
      playlist.length > 0
    ) {

      state.productBootstrapped =
        true;


      setPlaylist(
        playlist
      );


      loadWithoutPlaying(
        playlist[0]
      );


      configureProductCta();


      if (
        bootstrap.dataset
          .scProductAutoplay ===
        'true'
      ) {

        state.autoplayAttempted =
          true;


        window.setTimeout(
          () => {

            requestPlay(
              'product-autoplay',
              {
                autoplayAttempt:
                  true
              }
            );

          },
          120
        );
      }


      return;
    }


    const candidate =
      trackFromProductBootstrap(
        bootstrap
      );


    if (!candidate.src) {
      return;
    }


    state.productBootstrapped =
      true;


    clearPlaylist();


    loadWithoutPlaying(
      candidate
    );


    configureProductCta();
  };


  /* =========================================================
     CLICKS
  ========================================================= */

  const handleClick = (
    event
  ) => {
    const trigger =
      event.target.closest(
        TRIGGER_SELECTOR
      );


    if (trigger) {

      event.preventDefault();
      event.stopPropagation();

      toggleTrack(
        trigger
      );

      return;
    }


    const ui = getUI();

    if (!ui) return;


    const productCartCta =
      event.target.closest(
        '[data-sc-player-product-link][data-sc-product-cart-cta="true"]'
      );


    if (productCartCta) {

      event.preventDefault();
      event.stopPropagation();


      addCurrentProductToCart(
        productCartCta
      );


      return;
    }


    if (
      event.target.closest(
        '[data-sc-player-toggle]'
      )
    ) {

      event.preventDefault();


      if (!state.track) {
        return;
      }


      if (
        state.audio.paused ||
        state.audio.ended
      ) {

        requestPlay(
          'player'
        );

      } else {

        pauseFromUser(
          'player'
        );

      }


      return;
    }


    if (
      event.target.closest(
        '[data-sc-player-previous]'
      )
    ) {

      event.preventDefault();

      playPrevious(
        true
      );

      return;
    }


    if (
      event.target.closest(
        '[data-sc-player-next]'
      )
    ) {

      event.preventDefault();

      playNext(
        true
      );

      return;
    }


    if (
      event.target.closest(
        '[data-sc-player-mute]'
      )
    ) {

      event.preventDefault();

      toggleMute();

      return;
    }


    if (
      event.target.closest(
        '[data-sc-player-playlist-toggle]'
      )
    ) {

      event.preventDefault();

      togglePlaylist();

      return;
    }


    if (
      event.target.closest(
        '[data-sc-player-playlist-close]'
      )
    ) {

      event.preventDefault();

      closePlaylist();

      return;
    }


    const playlistTrack =
      event.target.closest(
        '[data-sc-playlist-track]'
      );


    if (playlistTrack) {

      event.preventDefault();


      const index =
        Number(
          playlistTrack.dataset
            .scPlaylistIndex
        );


      if (
        !Number.isInteger(index)
      ) {
        return;
      }


      if (
        index ===
        state.currentIndex
      ) {

        if (
          state.audio.paused ||
          state.audio.ended
        ) {

          requestPlay(
            'playlist'
          );

        } else {

          pauseFromUser(
            'playlist'
          );

        }


        return;
      }


      playPlaylistIndex(
        index,
        'playlist'
      );


      return;
    }


    const dock =
      event.target.closest(
        '[data-sc-player-dock]'
      );


    if (dock) {

      event.preventDefault();
      event.stopPropagation();


      if (!state.track) {
        return;
      }


      openPlayer();


      if (
        dock.dataset.scDockAction ===
          'resume' &&
        (
          state.audio.paused ||
          state.audio.ended
        )
      ) {

        requestPlay(
          'dock'
        );

      }


      return;
    }


    if (
      event.target.closest(
        '[data-sc-player-close]'
      )
    ) {

      event.preventDefault();

      closePlayer();

      return;
    }


    const productLink =
      event.target.closest(
        [
          '[data-sc-player-product-link]',
          '[data-sc-player-title-link]',
          '[data-sc-player-artwork-link]'
        ].join(', ')
      );


    if (
      productLink &&
      state.track
    ) {

      analyticsPayload(
        'audio_preview_view_product',
        {
          source:
            productLink.hasAttribute(
              'data-sc-player-product-link'
            )
              ? 'button'
              : productLink.hasAttribute(
                  'data-sc-player-artwork-link'
                )
                ? 'artwork'
                : 'title'
        }
      );
    }
  };


  /* =========================================================
     INPUTS
  ========================================================= */

  const handleInput = (
    event
  ) => {
    if (
      event.target.matches(
        '[data-sc-player-range]'
      )
    ) {

      seekFromRange(
        event.target
      );

      return;
    }


    if (
      event.target.matches(
        '[data-sc-player-volume]'
      )
    ) {

      setVolume(
        event.target.value
      );

    }
  };


  /* =========================================================
     AUDIO EVENTS
  ========================================================= */

  state.audio.addEventListener(
    'loadstart',
    () => {

      if (!state.track) {
        return;
      }


      state.loading =
        true;


      updatePlayerState();

    }
  );


  state.audio.addEventListener(
    'waiting',
    () => {

      if (
        !state.track ||
        state.audio.paused
      ) {
        return;
      }


      state.loading =
        true;


      updatePlayerState();

    }
  );


  state.audio.addEventListener(
    'canplay',
    () => {

      if (!state.track) {
        return;
      }


      state.loading =
        false;


      updatePlayerState();

    }
  );


  state.audio.addEventListener(
    'loadedmetadata',
    () => {

      state.loading =
        false;


      updateProgress();
      updateMetadataText();
      updatePlayerState();

    }
  );


  state.audio.addEventListener(
    'progress',
    updateProgress
  );


  state.audio.addEventListener(
    'playing',
    () => {

      if (!state.track) {
        return;
      }


      state.loading =
        false;


      updatePlayerState();


      if (
        state.playReportPending
      ) {

        state.playReportPending =
          false;


        analyticsPayload(
          'audio_preview_play',
          {
            source:
              state.playSource
          }
        );

      }
    }
  );


  state.audio.addEventListener(
    'pause',
    () => {

      if (
        state.switching
      ) {
        return;
      }


      state.loading =
        false;


      updatePlayerState();

    }
  );


  state.audio.addEventListener(
    'durationchange',
    updateProgress
  );


  state.audio.addEventListener(
    'timeupdate',
    () => {

      updateProgress();
      reportMilestones();

    }
  );


  state.audio.addEventListener(
    'ended',
    () => {

      state.loading =
        false;

      state.playReportPending =
        false;


      analyticsPayload(
        'audio_preview_complete',
        {
          milestone: 100
        }
      );


      const continued =
        playNext(
          true
        );


      if (continued) {
        return;
      }


      updatePlayerState();
      updateProgress();

    }
  );


  state.audio.addEventListener(
    'volumechange',
    updateVolumeUI
  );


  state.audio.addEventListener(
    'error',
    () => {

      const mediaError =
        state.audio.error;


      handlePlaybackError(
        new Error(
          mediaError
            ? `Media error code ${mediaError.code}`
            : 'Unknown media error'
        )
      );

    }
  );


  /* =========================================================
     DOCUMENT
  ========================================================= */

  document.addEventListener(
    'click',
    handleClick,
    true
  );


  document.addEventListener(
    'input',
    handleInput
  );


  document.addEventListener(
    'shopify:section:load',
    () => {

      queueTriggerSync();

      bootstrapProductPlayer();

    }
  );


  document.addEventListener(
    'keydown',
    (event) => {

      if (
        event.key !==
        'Escape'
      ) {
        return;
      }


      const ui =
        getUI();


      if (
        ui?.playlistPanel &&
        !ui.playlistPanel.hidden
      ) {

        closePlaylist();

        return;
      }


      if (
        getPlayer()?.classList.contains(
          'is-open'
        )
      ) {

        closePlayer();

      }

    }
  );


  /* =========================================================
     OBSERVER
  ========================================================= */

  const observer =
    new MutationObserver(
      (mutations) => {

        const previewButtonsAdded =
          mutations.some(
            (mutation) =>
              Array.from(
                mutation.addedNodes
              ).some(
                (node) => (
                  node.nodeType ===
                    Node.ELEMENT_NODE &&
                  (
                    node.matches?.(
                      TRIGGER_SELECTOR
                    ) ||
                    node.querySelector?.(
                      TRIGGER_SELECTOR
                    )
                  )
                )
              )
          );


        if (
          previewButtonsAdded
        ) {
          queueTriggerSync();
        }


        if (
          !state.productBootstrapped
        ) {

          const bootstrapAdded =
            mutations.some(
              (mutation) =>
                Array.from(
                  mutation.addedNodes
                ).some(
                  (node) => (
                    node.nodeType ===
                      Node.ELEMENT_NODE &&
                    (
                      node.matches?.(
                        PRODUCT_BOOTSTRAP_SELECTOR
                      ) ||
                      node.querySelector?.(
                        PRODUCT_BOOTSTRAP_SELECTOR
                      )
                    )
                  )
                )
            );


          if (
            bootstrapAdded
          ) {
            bootstrapProductPlayer();
          }

        }

      }
    );


  /* =========================================================
     START
  ========================================================= */

  const startObserver = () => {
    if (!document.body) {
      return;
    }


    observer.observe(
      document.body,
      {
        childList: true,
        subtree: true
      }
    );


    installListingPlayerTightFix();

    updateVolumeUI();

    bootstrapProductPlayer();
  };


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      startObserver,
      {
        once: true
      }
    );

  } else {

    startObserver();

  }


  window.addEventListener(
    'pagehide',
    () => {

      state.audio.pause();

      closePlaylist();

      hideDock();

    }
  );


  /* =========================================================
     PUBLIC API
  ========================================================= */

  window.SoulChemistAudioPreview = {

    pause: () =>
      pauseFromUser(
        'api'
      ),

    close:
      closePlayer,

    reopen:
      openPlayer,

    setVolume,

    next: () =>
      playNext(
        true
      ),

    previous: () =>
      playPrevious(
        true
      ),

    openPlaylist,

    closePlaylist,

    getCurrentTrack: () => (
      state.track
        ? {
            ...state.track
          }
        : null
    ),

    getPlaylist: () =>
      state.playlist.map(
        (track) => ({
          ...track
        })
      ),

    getCurrentIndex: () =>
      state.currentIndex
  };

})();